import { Icon, Stack, Text, Title } from "@assetwatch/ui";
import type { ProcedureLibraryRecord } from "../../queries/procedureLibraryAdapter";

type ProcedureGroupKey = "all" | "deleted" | "mine";

function ProcedureRow({
  procedure,
  selected,
  onSelect,
}: {
  procedure: ProcedureLibraryRecord;
  selected: boolean;
  onSelect: () => void;
}) {
  const category = procedure.category?.trim();

  return (
    <button
      aria-pressed={selected}
      className={`flex w-full items-start gap-(--spacing-12) border-b border-outline-subtle px-(--spacing-16) py-(--spacing-12) text-left outline-none transition-colors hover:bg-surface-container-low focus-visible:bg-surface-hover focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tertiary ${selected ? "border-l-2 border-l-primary bg-surface-container-low" : "border-l-2 border-l-transparent"}`}
      onClick={onSelect}
      type="button"
      data-selected={selected || undefined}
    >
      <span
        aria-hidden="true"
        className="mt-(--spacing-4) grid size-(--spacing-40) shrink-0 place-items-center rounded-(--radius-sm) border border-outline-subtle bg-surface-container text-text-subtle"
      >
        <Icon name="camera-regular" size="sm" />
      </span>
      <span className="min-w-0 flex-1">
        {category ? (
          <span className="block w-fit truncate rounded-(--radius-full) bg-primary-container px-(--spacing-8) py-(--spacing-2) text-(length:--font-size-label-small) text-primary-on-container">
            {category}
          </span>
        ) : null}
        <span className="mt-(--spacing-4) block truncate font-(number:--font-weight-semibold) text-text-default">
          {procedure.name}
        </span>
        <span className="mt-(--spacing-4) block truncate text-(length:--font-size-body-medium) text-text-subtle">
          {procedure.owner ?? "Unavailable"}
        </span>
      </span>
    </button>
  );
}

function ProcedureGroup({
  collapsed,
  label,
  onToggle,
  procedures,
  selectedId,
  onSelect,
}: {
  collapsed: boolean;
  label: string;
  onToggle: () => void;
  procedures: ProcedureLibraryRecord[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (procedures.length === 0) return null;
  const contentId = `procedure-group-${label.toLocaleLowerCase().replaceAll(" ", "-")}`;
  return (
    <div>
      <button
        aria-controls={contentId}
        aria-expanded={!collapsed}
        className="flex w-full items-center justify-between border-b border-outline-subtle bg-surface-container-low px-(--spacing-16) py-(--spacing-12) text-left text-text-default focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-button-focus-ring"
        onClick={onToggle}
        type="button"
      >
        <Text weight="semibold">
          {label} ({procedures.length})
        </Text>
        <span aria-hidden="true" className="text-text-subtle">
          {collapsed ? "›" : "⌄"}
        </span>
      </button>
      {!collapsed ? (
        <div id={contentId}>
          {procedures.map((procedure) => (
            <ProcedureRow
              key={procedure.id}
              onSelect={() => onSelect(procedure.id)}
              procedure={procedure}
              selected={procedure.id === selectedId}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

type ProcedureLibraryListProps = {
  allProcedures: ProcedureLibraryRecord[];
  deletedProcedures: ProcedureLibraryRecord[];
  mineProcedures: ProcedureLibraryRecord[];
  collapsedGroups: Record<ProcedureGroupKey, boolean>;
  onSelect: (id: string) => void;
  onToggleGroup: (group: ProcedureGroupKey) => void;
  selectedId: string | null;
};

export function ProcedureLibraryList({
  allProcedures,
  deletedProcedures,
  mineProcedures,
  collapsedGroups,
  onSelect,
  onToggleGroup,
  selectedId,
}: ProcedureLibraryListProps) {
  const hasProcedures = allProcedures.length > 0 || mineProcedures.length > 0;

  return (
    <div
      className={`${selectedId ? "hidden lg:flex" : "flex"} min-h-0 min-w-0 flex-col overflow-hidden rounded-(--radius-lg) border border-outline-subtle bg-surface shadow-elevation-1`}
      data-testid="procedures-library-pane"
    >
      <div
        className="min-h-0 flex-1 overflow-y-auto"
        data-testid="procedures-library-scroll-region"
      >
        {hasProcedures ? (
          <>
            <ProcedureGroup
              collapsed={collapsedGroups.mine}
              label="My Procedures"
              onSelect={onSelect}
              onToggle={() => onToggleGroup("mine")}
              procedures={mineProcedures}
              selectedId={selectedId}
            />
            <ProcedureGroup
              collapsed={collapsedGroups.all}
              label="All"
              onSelect={onSelect}
              onToggle={() => onToggleGroup("all")}
              procedures={allProcedures}
              selectedId={selectedId}
            />
          </>
        ) : (
          <div className="grid min-h-64 place-items-center p-(--spacing-24) text-center">
            <Stack align="center" gap={8}>
              <Title as="h2" size="lg">
                No procedures found
              </Title>
              <Text tone="subtle">Try a different title or Procedure ID.</Text>
            </Stack>
          </div>
        )}
        <ProcedureGroup
          collapsed={collapsedGroups.deleted}
          label="Deleted"
          onSelect={onSelect}
          onToggle={() => onToggleGroup("deleted")}
          procedures={deletedProcedures}
          selectedId={selectedId}
        />
      </div>
    </div>
  );
}
